import React, { useMemo, useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import { EditTable } from '@components';
import { EDIT_TABLE_COLUMNS_TYPE } from '@utils/enums';
import { useDispatch } from 'react-redux';
import actions from '@flux/action';
import { addShowCurrencyOnValueCol, resetCurrecyState } from './WaterfallGrid';
import Amount from '@utils/currency';
import './index.scss';

const notBlank = (str) => typeof str === 'string' && str.length > 0;

const duplicateNameInSameCategory = ({ getFieldsValue }) => ({
  validator(record) {
    const curRecordId = record.field.split('_')[1];
    if (!curRecordId) {
      return Promise.resolve();
    }
    const fieldsValue = getFieldsValue();

    let hasDuplicate = false;
    Object.keys(fieldsValue)
      .map((key) => key.split('_')[1])
      .forEach((recordId, index, a) => {
        if (
          !recordId ||
          // Deduplicate ids in the field list
          a.indexOf(recordId) !== index ||
          // Do not check self
          recordId === curRecordId ||
          // Check Duplicate Names in a same category
          (fieldsValue[`category_${recordId}`] !==
            fieldsValue[`category_${curRecordId}`] &&
            // Either field should not be empty
            notBlank(fieldsValue[`category_${recordId}`]) &&
            notBlank(fieldsValue[`category_${curRecordId}`])) ||
          fieldsValue[`name_${recordId}`] !== fieldsValue[`name_${curRecordId}`]
        ) {
          return;
        }

        hasDuplicate = true;
      });

    if (hasDuplicate) {
      return Promise.reject('Duplicate Names');
    }
    return Promise.resolve();
  },
});

const BarChartGridColumns = [
  {
    title: 'Category',
    dataIndex: 'category',
    editable: true,
    type: EDIT_TABLE_COLUMNS_TYPE.INPUT,
  },
  {
    title: 'Sub-category',
    dataIndex: 'name',
    required: true,
    editable: true,
    type: EDIT_TABLE_COLUMNS_TYPE.INPUT,
    compProps: {
      rules: [duplicateNameInSameCategory],
    },
  },
  {
    title: 'Value',
    dataIndex: 'value',
    required: true,
    editable: true,
    type: EDIT_TABLE_COLUMNS_TYPE.INPUT,
    compProps: {
      type: 'number',
      rules: [
        {
          required: true,
          message: `Value is required.`,
        },
      ],
      prefix: '$',
    },
    render: (text) =>
      Amount.format({ value: Number(text), currencyIsoCode: 'USD' }, -1),
  },
  {
    title: 'Description',
    dataIndex: 'comment',
    required: false,
    editable: true,
    type: EDIT_TABLE_COLUMNS_TYPE.INPUT,
    compProps: {
      rules: [
        ({ getFieldValue }) => ({
          validator(_, value) {
            if (value?.length > 500) {
              return Promise.reject('Character limit is 500.');
            }
            return Promise.resolve();
          },
        }),
      ],
    },
  },
];

export default function StackedBarChart({
  editTableRef,
  isEditMode,
  sourceData,
  handleSave: _handleSave,
  toggleEdit,
  link,
  afterCancel,
}) {
  const dispatch = useDispatch();

  const initialShowCurrency = sourceData.some((row) => row.showCurrency);
  const [showCurrency, setShowCurrency] = useState(
    sourceData.some((row) => row.showCurrency),
  );
  const showCurrencyRef = useRef(showCurrency);

  useEffect(() => {
    showCurrencyRef.current = showCurrency;
  }, [showCurrency]);

  const handleSave = (data) => {
    if (data[0]) {
      data[0].showCurrency = showCurrencyRef.current;
    }
    dispatch(actions.changeWidget({ widgetId: link, data: {} }));
    _handleSave(data);
  };

  const revalidateForm = () => {
    if (!editTableRef?.current?.validateFields) {
      return;
    }
    editTableRef.current.validateFields();
  };

  const editTableParams = useMemo(
    () => ({
      tableProps: {
        pagination: false,
        rowKey: 'id',
      },
      tableColumns: BarChartGridColumns.map(
        addShowCurrencyOnValueCol(isEditMode, showCurrency, setShowCurrency),
      ),
      defaultDataSource: sourceData,
      saveData: handleSave,
      toggleEdit,
      dataChange: revalidateForm,
      afterCancel: () => {
        resetCurrecyState(editTableRef, initialShowCurrency);
        if (typeof afterCancel === 'function') {
          afterCancel();
        }
      },
    }),
    [sourceData, isEditMode, initialShowCurrency],
  );

  return (
    <div
      className={classnames('stacked-bar-grid-wrapper', {
        'show-currency-wrapper': showCurrency,
        'hide-currency-wrapper': !showCurrency,
      })}
    >
      <EditTable ref={editTableRef} {...editTableParams} />
    </div>
  );
}
